import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown, Type, Diamond } from 'lucide-react';

interface FontOption {
  name: string;
  value: string;
  isPro?: boolean;
}

interface FontSelectProps {
  fonts: FontOption[];
  selectedFont: string;
  onChange: (font: string) => void;
  label?: string;
  darkMode?: boolean;
}

export const FontSelect: React.FC<FontSelectProps> = ({ fonts, selectedFont, onChange, label, darkMode = false }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const currentFont = fonts.find(f => f.value === selectedFont);
  const currentFontName = currentFont?.name || selectedFont;

  const bgClass = darkMode ? 'bg-slate-900' : 'bg-white';
  const borderClass = darkMode ? 'border-slate-700' : 'border-slate-200';
  const textClass = darkMode ? 'text-slate-200' : 'text-slate-900';
  const hoverClass = darkMode ? 'hover:bg-slate-800' : 'hover:bg-slate-50';
  const activeClass = darkMode ? 'bg-blue-900/30 text-blue-400' : 'bg-blue-50 text-blue-700';

  return (
    <div className="relative" ref={dropdownRef}>
      {label && <label className={`text-xs mb-1 block ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>{label}</label>}
      
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`w-full flex items-center justify-between p-2 border rounded text-sm transition-colors ${bgClass} ${borderClass} ${textClass} hover:border-blue-500`}
      >
        <span style={{ fontFamily: selectedFont }} className="truncate mr-2 flex items-center gap-2">
          {currentFontName}
          {currentFont?.isPro && <Diamond className="w-3 h-3 text-blue-400 fill-blue-400/20" />}
        </span>
        <ChevronDown className={`w-4 h-4 ${darkMode ? 'text-slate-500' : 'text-slate-400'}`} />
      </button>

      {isOpen && (
        <div className={`absolute z-50 w-64 -left-0 md:w-72 mt-1 border rounded-lg shadow-xl max-h-60 overflow-y-auto ${bgClass} ${borderClass}`}>
          {fonts.map((font) => (
            <button
              key={font.value}
              onClick={() => {
                onChange(font.value);
                setIsOpen(false);
              }}
              className={`w-full text-left px-3 py-2 text-sm flex items-center justify-between ${hoverClass} ${
                selectedFont === font.value ? activeClass : textClass
              }`}
            >
              <div className="flex items-center gap-2 overflow-hidden">
                  <Type className="w-3 h-3 opacity-30 flex-shrink-0" />
                  <span style={{ fontFamily: font.value, fontSize: '16px' }} className="truncate">
                    {font.name}
                  </span>
              </div>
              {font.isPro && <Diamond className="w-3 h-3 text-blue-400 fill-blue-400/20 flex-shrink-0" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};